/**
 * Specialized service-unavailable placeholder components.
 *
 * Each variant matches the visual layout of its corresponding section
 * (chart axes, donut ring, table rows, etc.) so the user sees a
 * recognisable shape with a "temporarily unavailable" message rather
 * than a generic error card or a spinner.
 *
 * All variants are fully static (no animation / no spinner) and use
 * muted colours (`primary-400`, `primary-100 / primary-800`).
 */

// -- Shared message component ------------------------------------------------

function UnavailableMessage({ message }: { message: string }) {
  return (
    <p className="text-center text-sm text-primary-400 dark:text-primary-500">
      {message}
    </p>
  )
}

// -- ChartUnavailable --------------------------------------------------------

export function ChartUnavailable({
  height = 200,
  message = 'Chart data is temporarily unavailable. Please refresh in a few minutes.',
}: {
  height?: number
  message?: string
}) {
  return (
    <div style={{ height }} className="flex flex-col">
      <svg
        className="w-full flex-1"
        viewBox="0 0 400 160"
        preserveAspectRatio="none"
        aria-hidden
      >
        {/* Y-axis */}
        <line
          x1="40"
          y1="10"
          x2="40"
          y2="140"
          className="stroke-primary-200 dark:stroke-primary-700"
          strokeWidth="1"
        />
        {/* X-axis */}
        <line
          x1="40"
          y1="140"
          x2="390"
          y2="140"
          className="stroke-primary-200 dark:stroke-primary-700"
          strokeWidth="1"
        />
        {/* Grid lines */}
        {[40, 72, 104].map((y) => (
          <line
            key={y}
            x1="40"
            y1={y}
            x2="390"
            y2={y}
            className="stroke-primary-100 dark:stroke-primary-800"
            strokeWidth="1"
            strokeDasharray="4 4"
          />
        ))}
      </svg>
      <UnavailableMessage message={message} />
    </div>
  )
}

// -- DonutUnavailable --------------------------------------------------------

export function DonutUnavailable({
  height = 200,
  message = 'Breakdown data is temporarily unavailable. Please refresh in a few minutes.',
}: {
  height?: number
  message?: string
}) {
  return (
    <div
      style={{ height }}
      className="flex flex-col items-center justify-center gap-2"
    >
      <svg
        width="120"
        height="120"
        viewBox="0 0 120 120"
        aria-hidden
        className="shrink-0"
      >
        <circle
          cx="60"
          cy="60"
          r="50"
          fill="none"
          className="stroke-primary-200 dark:stroke-primary-700"
          strokeWidth="12"
          strokeDasharray="6 6"
        />
      </svg>
      <UnavailableMessage message={message} />
    </div>
  )
}

// -- RankingUnavailable ------------------------------------------------------

export function RankingUnavailable({
  height = 280,
  rows = 5,
  message = 'Ranking data is temporarily unavailable. Please refresh in a few minutes.',
}: {
  height?: number
  rows?: number
  message?: string
}) {
  return (
    <div style={{ minHeight: height }} className="relative overflow-hidden">
      {/* Header row */}
      <div className="flex items-center border-b border-primary-200 bg-primary-50/50 px-2 py-1 dark:border-primary-700 dark:bg-primary-800/50">
        <span className="h-3 w-6 shrink-0 rounded bg-primary-100 dark:bg-primary-800" />
        <span className="ml-2 h-3 flex-1 rounded bg-primary-100 dark:bg-primary-800" />
        <span className="ml-2 h-3 w-16 shrink-0 rounded bg-primary-100 dark:bg-primary-800" />
      </div>

      {/* Empty rows with faded bar placeholders */}
      <div className="divide-y divide-primary-100 dark:divide-primary-800">
        {Array.from({ length: rows }, (_, i) => (
          <div key={i} className="flex items-center px-2 py-2.5">
            <span className="h-3 w-6 shrink-0 rounded bg-primary-100 dark:bg-primary-800" />
            <div className="relative ml-2 flex-1">
              <div
                className="h-3 rounded bg-primary-100 dark:bg-primary-800"
                style={{ width: `${80 - i * 12}%` }}
              />
            </div>
            <span className="ml-2 h-3 w-12 shrink-0 rounded bg-primary-100 dark:bg-primary-800" />
          </div>
        ))}
      </div>

      {/* Centered overlay message */}
      <div className="absolute inset-0 flex items-center justify-center bg-white/60 dark:bg-primary-900/60">
        <UnavailableMessage message={message} />
      </div>
    </div>
  )
}

// -- MapUnavailable ----------------------------------------------------------

export function MapUnavailable({
  height = 320,
  message = 'Geographic data is temporarily unavailable. Please refresh in a few minutes.',
}: {
  height?: number
  message?: string
}) {
  return (
    <div
      style={{ height }}
      className="relative flex items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-800"
    >
      {/* Simplified US outline */}
      <svg viewBox="0 0 960 600" className="h-3/4 w-3/4 opacity-20" aria-hidden>
        <path
          d="M230 140 L340 120 L460 100 L580 90 L680 95 L780 130 L820 170 L840 250 L830 330 L800 390 L760 430 L700 470 L620 490 L530 500 L440 490 L360 470 L280 430 L220 380 L180 310 L170 240 L190 180 Z"
          fill="none"
          className="stroke-primary-300 dark:stroke-primary-600"
          strokeWidth="3"
        />
      </svg>

      {/* Centered message */}
      <div className="absolute inset-0 flex items-center justify-center">
        <UnavailableMessage message={message} />
      </div>
    </div>
  )
}

// -- BarListUnavailable ------------------------------------------------------

export function BarListUnavailable({
  height = 200,
  bars = 4,
  message = 'Data is temporarily unavailable. Please refresh in a few minutes.',
}: {
  height?: number
  bars?: number
  message?: string
}) {
  const widths = [85, 62, 45, 30, 20]

  return (
    <div
      style={{ minHeight: height }}
      className="flex flex-col justify-between"
    >
      <div className="space-y-3">
        {Array.from({ length: bars }, (_, i) => (
          <div key={i}>
            <div className="mb-1 flex items-center justify-between">
              <span
                className="h-3 rounded bg-primary-100 dark:bg-primary-800"
                style={{ width: `${40 + i * 5}%` }}
              />
              <span className="h-3 w-10 rounded bg-primary-100 dark:bg-primary-800" />
            </div>
            <div className="relative h-2 w-full overflow-hidden rounded-full bg-primary-100 dark:bg-primary-800">
              <div
                className="h-full rounded-full bg-primary-200 dark:bg-primary-700"
                style={{ width: `${widths[i] ?? 20}%` }}
              />
            </div>
          </div>
        ))}
      </div>
      <div className="mt-3">
        <UnavailableMessage message={message} />
      </div>
    </div>
  )
}

// -- InsightsUnavailable -----------------------------------------------------

export function InsightsUnavailable({
  message = 'Insights are temporarily unavailable. Please refresh in a few minutes.',
}: {
  message?: string
}) {
  return (
    <div className="relative flex flex-col rounded-xl border border-primary-200 bg-white shadow-sm dark:border-primary-700 dark:bg-primary-900">
      {/* Header */}
      <div className="flex items-center border-b border-primary-100 px-3 py-2 sm:px-4 sm:py-2.5 dark:border-primary-800">
        <span className="h-3 w-24 rounded bg-primary-100 dark:bg-primary-800" />
      </div>
      {/* Muted rows */}
      <div className="p-3 sm:p-4">
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="flex items-start gap-3 border-b border-primary-200/50 pb-2 last:border-0 last:pb-0 dark:border-primary-700/50"
            >
              <span className="mt-0.5 h-4 w-4 shrink-0 rounded bg-primary-100 dark:bg-primary-800" />
              <span className="h-4 w-14 shrink-0 rounded bg-primary-100 dark:bg-primary-800" />
              <span className="h-4 flex-1 rounded bg-primary-100 dark:bg-primary-800" />
            </div>
          ))}
        </div>
      </div>

      {/* Overlay */}
      <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-white/60 dark:bg-primary-900/60">
        <UnavailableMessage message={message} />
      </div>
    </div>
  )
}

// -- CardGridUnavailable -----------------------------------------------------

export function CardGridUnavailable({
  message = 'Officer data is temporarily unavailable. Please refresh in a few minutes.',
}: {
  message?: string
}) {
  return (
    <div className="relative">
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="flex items-center gap-3 rounded-lg border border-primary-200 bg-primary-50/50 p-3 dark:border-primary-700 dark:bg-primary-800/30"
          >
            {/* Avatar circle */}
            <div className="h-10 w-10 shrink-0 rounded-lg bg-primary-200 dark:bg-primary-700" />
            {/* Text lines */}
            <div className="min-w-0 flex-1 space-y-1.5">
              <div className="h-3 w-3/4 rounded bg-primary-200 dark:bg-primary-700" />
              <div className="h-2.5 w-1/2 rounded bg-primary-100 dark:bg-primary-800" />
            </div>
          </div>
        ))}
      </div>

      {/* Overlay */}
      <div className="absolute inset-0 flex items-center justify-center bg-white/60 dark:bg-primary-900/60">
        <UnavailableMessage message={message} />
      </div>
    </div>
  )
}

// -- TableUnavailable --------------------------------------------------------

export function TableUnavailable({
  message = 'Award data is temporarily unavailable. Please refresh in a few minutes.',
}: {
  message?: string
}) {
  return (
    <div className="relative overflow-hidden rounded-lg border border-primary-200 dark:border-primary-700">
      <table className="w-full text-left text-sm">
        <thead className="border-b border-primary-200 bg-primary-50 dark:border-primary-700 dark:bg-primary-800">
          <tr>
            {['Award ID', 'Date', 'Agency', 'Type', 'Amount'].map((col) => (
              <th
                key={col}
                className="px-4 py-3 text-xs font-semibold uppercase text-primary-300 dark:text-primary-600"
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-primary-100 dark:divide-primary-800">
          {[1, 2, 3].map((i) => (
            <tr key={i} className="bg-white dark:bg-primary-900">
              <td className="px-4 py-3">
                <div className="h-4 w-24 rounded bg-primary-100 dark:bg-primary-800" />
              </td>
              <td className="px-4 py-3">
                <div className="h-4 w-20 rounded bg-primary-100 dark:bg-primary-800" />
              </td>
              <td className="px-4 py-3">
                <div className="h-4 w-32 rounded bg-primary-100 dark:bg-primary-800" />
              </td>
              <td className="px-4 py-3">
                <div className="h-4 w-16 rounded bg-primary-100 dark:bg-primary-800" />
              </td>
              <td className="px-4 py-3 text-right">
                <div className="ml-auto h-4 w-20 rounded bg-primary-100 dark:bg-primary-800" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Overlay */}
      <div className="absolute inset-0 flex items-center justify-center bg-white/60 dark:bg-primary-900/60">
        <UnavailableMessage message={message} />
      </div>
    </div>
  )
}

// -- OpportunitiesUnavailable ------------------------------------------------

export function OpportunitiesUnavailable({
  message = 'Opportunities data is temporarily unavailable. Please refresh in a few minutes.',
}: {
  message?: string
}) {
  return (
    <div className="relative">
      {/* Summary pills */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 rounded-lg border border-primary-200 bg-primary-50/50 px-3 py-2 dark:border-primary-700 dark:bg-primary-800/30">
          <div className="h-4 w-4 rounded bg-primary-200 dark:bg-primary-700" />
          <div className="h-4 w-8 rounded bg-primary-200 dark:bg-primary-700" />
          <div className="h-3 w-20 rounded bg-primary-100 dark:bg-primary-800" />
        </div>
        <div className="flex items-center gap-2 rounded-lg border border-primary-200 bg-primary-50/50 px-3 py-2 dark:border-primary-700 dark:bg-primary-800/30">
          <div className="h-4 w-4 rounded bg-primary-200 dark:bg-primary-700" />
          <div className="h-4 w-8 rounded bg-primary-200 dark:bg-primary-700" />
          <div className="h-3 w-24 rounded bg-primary-100 dark:bg-primary-800" />
        </div>
      </div>

      {/* Card outlines */}
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="rounded-lg border border-primary-200 bg-white p-4 dark:border-primary-700 dark:bg-primary-800/50"
          >
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <div className="h-5 w-16 rounded-full bg-primary-100 dark:bg-primary-800" />
              <div className="h-5 w-20 rounded-full bg-primary-100 dark:bg-primary-800" />
            </div>
            <div className="mb-1 h-4 w-3/4 rounded bg-primary-100 dark:bg-primary-800" />
            <div className="mb-3 h-3 w-40 rounded bg-primary-100 dark:bg-primary-800" />
            <div className="flex flex-wrap items-center gap-4">
              <div className="h-3 w-28 rounded bg-primary-100 dark:bg-primary-800" />
              <div className="h-3 w-28 rounded bg-primary-100 dark:bg-primary-800" />
            </div>
          </div>
        ))}
      </div>

      {/* Overlay */}
      <div className="absolute inset-0 flex items-center justify-center bg-white/60 dark:bg-primary-900/60">
        <UnavailableMessage message={message} />
      </div>
    </div>
  )
}
